<template>
  <UiDialog v-model:open="isOpen" title="Log in to Geobu">
    <div class="login-modal-body flex flex-col items-center text-center p-2">
      <!-- Error Alert -->
      <div
        v-if="error"
        class="w-full p-3 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-500 mb-4 flex items-center gap-2 text-left"
      >
        <i class="fa-solid fa-circle-exclamation text-sm shrink-0"></i>
        <span>{{ error }}</span>
      </div>

      <!-- State: Idle (Single Login Button + Native Widget) -->
      <div v-if="state === 'idle'" class="w-full flex flex-col items-center">
        <div class="telegram-icon-header w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center text-3xl mb-4 shadow-md">
          <i class="fa-brands fa-telegram"></i>
        </div>

        <h3 class="text-base sm:text-lg font-bold mb-1">Telegram Authentication</h3>
        <p class="text-xs sm:text-sm text-gray-500 mb-6 max-w-xs leading-relaxed">
          Log in with your Telegram account to sync your flashcard decks, learning progress, and personal words.
        </p>

        <!-- Native Telegram OAuth Login Button -->
        <button
          type="button"
          class="btn-telegram-login w-full py-3 px-6 rounded-xl bg-blue-500 hover:bg-blue-600 active:scale-98 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-md transition"
          :disabled="loading"
          @click="startLogin"
        >
          <i v-if="loading" class="fa-solid fa-spinner fa-spin text-base"></i>
          <i v-else class="fa-brands fa-telegram text-lg"></i>
          <span>Log In Through Telegram</span>
        </button>

        <p class="text-center text-xs text-gray-400 mt-2.5">
          Opens native Telegram authorization to verify your account automatically.
        </p>

        <!-- Official Telegram Login Widget alternative -->
        <div class="mt-6 pt-5 border-t border-gray-100 dark:border-gray-800 w-full flex flex-col items-center">
          <p class="text-xs text-gray-400 mb-3">Or log in with official Telegram widget:</p>
          <div ref="widgetContainerRef" class="telegram-widget-box min-h-10 flex items-center justify-center"></div>
        </div>
      </div>

      <!-- State: Waiting for Telegram Confirmation -->
      <div v-else-if="state === 'waiting'" class="text-center py-2 flex flex-col items-center w-full">
        <div class="telegram-pulsing-icon my-2">
          <i class="fa-brands fa-telegram text-5xl text-blue-500 animate-pulse"></i>
        </div>

        <h3 class="text-base font-bold mt-3">Confirm in Telegram</h3>
        <p class="text-xs text-gray-500 my-2 max-w-xs leading-relaxed">
          A Telegram authorization window has opened. Tap <strong class="text-blue-500">Log in</strong> on the Telegram prompt to continue.
        </p>

        <div class="flex flex-col gap-2 my-4 w-full">
          <UiButton
            variant="secondary"
            class="w-full justify-center text-xs"
            @click="reopenPopup"
          >
            <i class="fa-solid fa-arrow-up-right-from-square mr-1.5"></i>
            Reopen Telegram Login Window
          </UiButton>
        </div>

        <div class="flex items-center gap-2 text-xs text-gray-400 mb-4">
          <UiSpinner />
          <span>Waiting for confirmation... ({{ formatCountdown(countdown) }})</span>
        </div>

        <UiButton
          variant="ghost"
          class="w-full justify-center text-xs"
          @click="cancelLogin"
        >
          Cancel
        </UiButton>
      </div>
    </div>
  </UiDialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { UiDialog, UiButton, UiSpinner } from '../ui';
import { api } from '../services/api';
import { useAuth } from '../composables/useAuth';

const isOpen = defineModel<boolean>('open', { default: false });
const { setUser } = useAuth();

const state = ref<'idle' | 'waiting'>('idle');
const loading = ref(false);
const error = ref('');
const oauthUrl = ref('');
const countdown = ref(300);

let popupWindow: Window | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let currentToken = '';

const widgetContainerRef = ref<HTMLDivElement | null>(null);

const emit = defineEmits<{
  'login-success': [];
}>();

function completeOAuthLogin(user: any, token: string) {
  cleanup();
  if (popupWindow && !popupWindow.closed) {
    try {
      popupWindow.close();
    } catch {
      // ignore
    }
  }
  setUser(user, token);
  isOpen.value = false;
  emit('login-success');
}

function onPopupMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin) return;
  const data = event.data;
  if (!data || typeof data !== 'object') return;
  if (data.type === 'geobu:oauth-success' && data.token && data.user) {
    completeOAuthLogin(data.user, data.token);
  } else if (data.type === 'geobu:oauth-error') {
    cleanup();
    state.value = 'idle';
    error.value = data.error || 'Telegram authorization failed.';
  }
}

function onStorageEvent(event: StorageEvent) {
  if (event.key === 'geobu:token' && event.newValue && state.value === 'waiting') {
    const rawUser = localStorage.getItem('geobu:user');
    if (rawUser) {
      try {
        const parsedUser = JSON.parse(rawUser);
        completeOAuthLogin(parsedUser, event.newValue);
      } catch {
        // ignore
      }
    }
  }
}

function cleanup() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (countdownTimer !== null) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
  window.removeEventListener('message', onPopupMessage);
  window.removeEventListener('storage', onStorageEvent);
}

function formatCountdown(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function reopenPopup() {
  if (oauthUrl.value) {
    popupWindow = window.open(
      oauthUrl.value,
      'telegram-oauth-login',
      'popup,width=520,height=720,menubar=no,toolbar=no,location=no,status=no'
    );
  }
}

function cancelLogin() {
  cleanup();
  if (popupWindow && !popupWindow.closed) {
    try {
      popupWindow.close();
    } catch {
      // ignore
    }
  }
  state.value = 'idle';
  loading.value = false;
}

async function startLogin() {
  loading.value = true;
  error.value = '';
  try {
    const res = await api.startTelegramOAuth();
    currentToken = res.token;
    oauthUrl.value = res.url;
    state.value = 'waiting';

    window.addEventListener('message', onPopupMessage);
    window.addEventListener('storage', onStorageEvent);

    // Propagate browser-binding cookie client-side as well
    if (res.browser_cookie) {
      const domainAttr = res.cookie_domain ? `; domain=${res.cookie_domain}` : '';
      document.cookie = `tm_tg_browser=${res.browser_cookie}${domainAttr}; path=/; max-age=600; Secure; SameSite=Lax`;
      document.cookie = `tm_tg_browser=${res.browser_cookie}${domainAttr}; path=/api/auth/; max-age=600; Secure; SameSite=Lax`;
    }

    // Open Telegram OAuth authorization window
    popupWindow = window.open(
      res.url,
      'telegram-oauth-login',
      'popup,width=520,height=720,menubar=no,toolbar=no,location=no,status=no'
    );

    // Start countdown
    countdown.value = 300;
    countdownTimer = setInterval(() => {
      if (countdown.value > 0) {
        countdown.value--;
      } else {
        cancelLogin();
        error.value = 'Login session expired. Please try again.';
      }
    }, 1000);

    // Start polling status every 1500ms
    pollTimer = setInterval(async () => {
      try {
        const statusRes = await api.checkTelegramOAuthStatus(currentToken);
        if (statusRes.status === 'approved' && statusRes.user && statusRes.token) {
          completeOAuthLogin(statusRes.user, statusRes.token);
        } else if (statusRes.status === 'rejected') {
          cleanup();
          if (popupWindow && !popupWindow.closed) {
            try {
              popupWindow.close();
            } catch {
              // ignore
            }
          }
          state.value = 'idle';
          error.value = statusRes.error || 'Access denied in Telegram.';
        }
      } catch {
        // keep polling
      }
    }, 1500);
  } catch (err: any) {
    loading.value = false;
    error.value = err.message || 'Failed to initialize Telegram login.';
  } finally {
    loading.value = false;
  }
}

const renderTelegramWidget = () => {
  if (!widgetContainerRef.value) return;
  widgetContainerRef.value.innerHTML = '';

  (window as any).onTelegramAuth = (user: any) => {
    api
      .loginWithTelegramWidget(user)
      .then((res) => {
        setUser(res.user, res.token);
        isOpen.value = false;
        emit('login-success');
      })
      .catch((err) => {
        error.value = err.message || 'Telegram widget login error';
      });
  };

  const script = document.createElement('script');
  script.src = 'https://telegram.org/js/telegram-widget.js?22';
  script.setAttribute('data-telegram-login', 'IgorLearnWordsDevBot');
  script.setAttribute('data-size', 'medium');
  script.setAttribute('data-onauth', 'onTelegramAuth(user)');
  script.setAttribute('data-request-access', 'write');
  script.async = true;
  widgetContainerRef.value.appendChild(script);
};

watch(isOpen, (open) => {
  if (open) {
    state.value = 'idle';
    error.value = '';
    renderTelegramWidget();
  } else {
    cleanup();
  }
});

onBeforeUnmount(() => {
  cleanup();
});
</script>

<style scoped>
.telegram-widget-box {
  min-height: 40px;
}
</style>
